// src/services/eventTracking.js - IMPROVED BEST PRACTICES VERSION

import { SESSION_ID, API_URL, apiClient } from "../api/apiConfig";

/**
 * EventTracker centralizes client-side analytics for the app. It currently tracks:
 * 1. Page lifecycle events (`page_load`, `page_unload`) and visibility changes.
 * 2. User interactions such as clicks, form submissions, input focus/blur, and input changes (without capturing values).
 * 3. Media interactions including video play, pause, and ended events.
 * 4. Scroll depth milestones and window-level errors/unhandled rejections.
 * 5. Session/member updates to keep the tracker aligned with authenticated state.
 */

class EventTracker {
	constructor() {
		this.eventQueue = []; // Only used for failed events that need retry
		this.sessionId = null;
		this.memberId = null;
		this.isInitialized = false;

		// Store listener references for cleanup
		this.listeners = {
			click: null,
			submit: null,
			play: null,
			pause: null,
			ended: null,
			scroll: null,
			visibilitychange: null,
			beforeunload: null,
			inputFocus: null,
			inputBlur: null,
			inputChange: null,
			selectionChange: null,
			dropdownClick: null,
			error: null,
			unhandledrejection: null,
		};

		// No timer - events sent immediately on user interactions

		// Scroll depth state (reset per page)
		this.scrollDepth = 0;

		// Video event deduplication - prevent duplicate tracking within 1 second
		this.lastVideoEvents = new Map(); // Map<videoUrl_eventType, timestamp>
		// Track last known currentTime for each video to detect loops/replays
		this.lastVideoPlayTimes = new Map(); // Map<videoUrl, lastCurrentTime>
		// Track which videos have already had their initial play event tracked
		// This prevents infinite tracking for looping videos
		this.trackedVideoPlays = new Set(); // Set<videoUrl>

		// Feature usage detection state
		this.featureUsagePatterns = {
			// Common feature patterns to detect automatically
			create: /create|add|new|make/i,
			delete: /delete|remove|trash/i,
			edit: /edit|update|modify|change/i,
			save: /save|store|keep/i,
			share: /share|send|forward/i,
			export: /export|download|save as/i,
			import: /import|upload|load/i,
			filter: /filter|search|find/i,
			sort: /sort|order|arrange/i,
			reset: /reset|clear|refresh/i,
		};

		// Load any failed events from localStorage (for retry)
		this.loadQueue();
	}

	/**
	 * Initialize the tracker with session and member info
	 */
	async initialize(sessionId, memberId) {
		// Prevent duplicate initialization
		if (this.isInitialized) {
			// console.warn(
			// 	"EventTracker already initialized. Use updateSession() instead."
			// );
			return;
		}

		this.sessionId = sessionId;
		this.memberId = memberId;
		this.isInitialized = true;

		// Filter old events from localStorage that don't match current session
		// This prevents sending events from previous pages/sessions
		if (this.eventQueue.length > 0) {
			const currentUrl = window.location.href;
			this.eventQueue = this.eventQueue.filter((event) => {
				// Only keep events from current session or events that haven't been sent
				return (
					event.sessionId === sessionId ||
					event.pageUrl === currentUrl ||
					event.eventType === "page_unload" // Keep unload events for debugging
				);
			});

			// If we filtered out events, save the cleaned queue
			if (this.eventQueue.length > 0) {
				// console.log(
				// 	`Filtered queue: ${this.eventQueue.length} events matching current session`
				// );
				this.saveQueue();
			}
		}

		// Track page load on initialization
		this.trackEvent({
			eventType: "page_load",
			pageUrl: window.location.href,
			pageTitle: document.title,
			refererUrl: document.referrer,
		});

		// Also track as page view (duplicate event type for analytics compatibility)
		this.trackEvent({
			eventType: "page_view",
			elementName: document.title || window.location.pathname,
			pageUrl: window.location.href,
			pageTitle: document.title,
			additionalData: {
				refererUrl: document.referrer,
				pathname: window.location.pathname,
				search: window.location.search,
			},
		});

		// Set up global event listeners (only once)
		this.setupGlobalListeners();
	}

	/**
	 * Track a custom event
	 * Note: Always uses current page URL at call time (not default parameters)
	 * to prevent stale URLs when navigating between pages
	 */
	trackEvent({
		eventType,
		elementId = null,
		elementName = null,
		pageUrl = null, // Don't use default - always get current at call time
		pageTitle = null,
		videoUrl = null,
		refererUrl = null,
		additionalData = {},
	}) {
		if (!this.isInitialized) {
			// console.warn("EventTracker not initialized. Call initialize() first.");
			return;
		}

		// Always use current page info at call time (not from defaults)
		// This ensures events always have the correct pageUrl for the current page
		const currentPageUrl = window.location.href;
		const currentPageTitle = document.title;
		const currentReferer = document.referrer;

		const event = {
			sessionId: this.sessionId,
			memberId: this.memberId,
			eventType,
			elementId,
			elementName,
			pageUrl: pageUrl || currentPageUrl, // Always use current if not explicitly provided
			pageTitle: pageTitle || currentPageTitle,
			videoUrl,
			refererUrl: refererUrl || currentReferer,
			createdAt: new Date().toISOString(),
			...additionalData,
		};

		// Send event immediately in real-time (no queuing/batching)
		// console.log("📊 Event tracked:", eventType);

		// Send immediately - no queuing
		this.sendEvent(event);
	}

	/**
	 * Set up global event listeners for automatic tracking
	 */
	setupGlobalListeners() {
		// Shared handler for both click and touch events to ensure mobile compatibility
		// Touch events are added to capture mobile interactions that might not trigger clicks
		const handleInteraction = (e, isTouch = false) => {
			const { target } = e;

			// Skip tracking if clicking on a dropdown menu item (handled by dropdownItemClick)
			const menuItem = target.closest(
				'[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
			);
			if (menuItem) {
				return; // Dropdown item clicks are handled by dropdownItemClick listener
			}

			// Check for dropdown triggers first (Radix UI patterns)
			// Radix UI DropdownMenuTrigger renders as button with data-radix-dropdown-menu-trigger
			const dropdownTrigger =
				target.closest(
					'button[data-radix-dropdown-menu-trigger], button[data-radix-popover-trigger], [data-radix-dropdown-menu-trigger], [data-radix-popover-trigger], button[aria-haspopup="menu"], [role="button"][aria-haspopup="menu"]'
				) ||
				// Also check if clicking Avatar or image inside a dropdown trigger
				target
					.closest(
						'img[alt*="avatar"], img[alt*="Avatar"], [class*="Avatar"], [class*="avatar"], [role="img"]'
					)
					?.closest('button, [role="button"]');

			if (dropdownTrigger) {
				// Track dropdown trigger click
				const triggerId = dropdownTrigger.id || null;
				const triggerName =
					dropdownTrigger.getAttribute("aria-label") ||
					dropdownTrigger.getAttribute("data-label") ||
					dropdownTrigger.getAttribute("alt") ||
					// Try to find text or image alt in children
					dropdownTrigger.querySelector("img[alt]")?.getAttribute("alt") ||
					dropdownTrigger.textContent?.trim().substring(0, 50) ||
					"dropdown_trigger";

				// Determine event type based on element tag
				const tagName = dropdownTrigger.tagName.toLowerCase();
				const eventType = `${tagName}-click`;

				this.trackEvent({
					eventType,
					elementId: triggerId,
					elementName: triggerName,
					additionalData: {
						tagName: dropdownTrigger.tagName,
						isDropdownTrigger: true,
						ariaHaspopup: dropdownTrigger.getAttribute("aria-haspopup"),
						ariaExpanded: dropdownTrigger.getAttribute("aria-expanded"),
						hasRadixTrigger:
							!!dropdownTrigger.getAttribute(
								"data-radix-dropdown-menu-trigger"
							) || !!dropdownTrigger.getAttribute("data-radix-popover-trigger"),
					},
				});
				// Return early to prevent duplicate tracking as regular interactive element
				return;
			}

			// Only track meaningful elements
			const interactiveElement = target.closest(
				'button, a, input, select, textarea, [role="button"], [role="tab"], [role="option"], [data-track], [data-selection]'
			);
			if (!interactiveElement) {
				// Check for custom selection patterns (like party vibes - clickable divs that toggle selection)
				const selectionElement = target.closest(
					'[data-selection-item], [class*="selected"], [class*="selection"]'
				);
				if (selectionElement) {
					const wasSelected =
						selectionElement.classList.contains("selected") ||
						selectionElement.getAttribute("aria-selected") === "true" ||
						selectionElement.classList.contains("bg-gradient-to-r"); // Common pattern for selected state

					const selectionValue =
						selectionElement.getAttribute("data-selection-value") ||
						selectionElement.getAttribute("data-value") ||
						selectionElement.textContent?.trim() ||
						null;

					this.trackEvent({
						eventType: "selection_change",
						elementId: selectionElement.id || null,
						elementName:
							selectionElement.getAttribute("data-selection-name") ||
							selectionElement.getAttribute("aria-label") ||
							selectionElement.textContent?.trim().substring(0, 50) ||
							"custom_selection",
						additionalData: {
							selectionType: "custom",
							selectedValue: selectionValue,
							wasSelected,
							action: wasSelected ? "deselected" : "selected",
							elementClasses: selectionElement.className || null,
						},
					});
				}
				return;
			}

			const elementId = interactiveElement.id || null;
			const elementName =
				interactiveElement.getAttribute("data-track") ||
				interactiveElement.getAttribute("aria-label") ||
				interactiveElement.getAttribute("name") ||
				interactiveElement.textContent?.trim().substring(0, 50) ||
				interactiveElement.tagName;

			// Detect feature usage patterns (common feature patterns)
			const isFeatureUsage =
				interactiveElement.getAttribute("data-feature") ||
				interactiveElement.getAttribute("data-action") ||
				elementName?.match(
					/^(create|add|delete|edit|update|save|share|export|import|download|upload|filter|sort|reset)/i
				);

			// Determine event type based on element tag
			const tagName = interactiveElement.tagName.toLowerCase();
			const eventType = `${tagName}-click`;

			this.trackEvent({
				eventType,
				elementId,
				elementName,
				additionalData: {
					tagName: interactiveElement.tagName,
					type: interactiveElement.type || null,
				},
			});

			// Track feature usage automatically
			if (isFeatureUsage) {
				const featureName =
					interactiveElement.getAttribute("data-feature") ||
					interactiveElement.getAttribute("data-action") ||
					elementName?.toLowerCase().split(/[\s_-]+/)[0] ||
					"unknown_feature";

				this.trackEvent({
					eventType: "feature_usage",
					elementId,
					elementName: `${featureName}_used`,
					additionalData: {
						featureName,
						action: "used",
						interactionType: "click",
					},
				});
			}
		};

		// Set up click listener
		this.listeners.click = (e) => handleInteraction(e, false);
		document.addEventListener("click", this.listeners.click, true);

		// Also listen to touchstart for better mobile support
		// This ensures we capture interactions even if click events are prevented
		this.listeners.touchstart = (e) => {
			// Only track if it's likely a tap (not a scroll start)
			// We use touchstart instead of touchend to avoid double-tracking with click
			// For most interactions, click will still fire, but touchstart ensures mobile coverage
			const { target } = e;
			const touch = e.touches[0];

			// Store touch info for potential use in click handler
			target._touchStartTime = Date.now();
			target._touchStartPosition = touch
				? { x: touch.clientX, y: touch.clientY }
				: null;
		};
		document.addEventListener("touchstart", this.listeners.touchstart, {
			passive: true,
			capture: true,
		});

		// Track form submissions
		this.listeners.submit = (e) => {
			const form = e.target;
			const formName =
				form.getAttribute("name") ||
				form.getAttribute("data-track") ||
				form.getAttribute("id") ||
				"unnamed_form";

			// Track form submit
			this.trackEvent({
				eventType: "form_submit",
				elementId: form.id,
				elementName: formName,
				additionalData: {
					formMethod: form.method || "get",
					formAction: form.action || null,
					fieldCount: form.querySelectorAll("input, select, textarea").length,
				},
			});
		};
		document.addEventListener("submit", this.listeners.submit, true);

		// Track input interactions (privacy-safe - no actual values stored)
		this.listeners.inputFocus = (e) => {
			// Check if target is an Element and has matches method
			if (
				e.target &&
				typeof e.target.matches === "function" &&
				e.target.matches(
					'input:not([type="password"]):not([type="hidden"]), textarea, select'
				)
			) {
				this.trackEvent({
					eventType: "input_focus",
					elementId: e.target.id,
					elementName:
						e.target.name ||
						e.target.placeholder ||
						e.target.getAttribute("aria-label") ||
						"unnamed_input",
					additionalData: {
						inputType: e.target.type || e.target.tagName.toLowerCase(),
						formId: e.target.form?.id || null,
						formName: e.target.form?.name || null,
					},
				});
			}
		};
		document.addEventListener("focus", this.listeners.inputFocus, true);

		// Track when user finishes with input (blur = leaves field)
		this.listeners.inputBlur = (e) => {
			// Check if target is an Element and has matches method
			if (
				e.target &&
				typeof e.target.matches === "function" &&
				e.target.matches(
					'input:not([type="password"]):not([type="hidden"]), textarea, select'
				)
			) {
				this.trackEvent({
					eventType: "input_blur",
					elementId: e.target.id,
					elementName:
						e.target.name ||
						e.target.placeholder ||
						e.target.getAttribute("aria-label") ||
						"unnamed_input",
					additionalData: {
						inputType: e.target.type || e.target.tagName.toLowerCase(),
						hasValue: !!e.target.value,
						valueLength: e.target.value?.length || 0, // Privacy: length only, not content
						formId: e.target.form?.id || null,
						formName: e.target.form?.name || null,
					},
				});
			}
		};
		document.addEventListener("blur", this.listeners.inputBlur, true);

		// Track input changes (debounced to avoid spam)
		const inputChangeTimers = new Map();
		this.listeners.inputChange = (e) => {
			// Check if target is an Element and has matches method
			if (
				e.target &&
				typeof e.target.matches === "function" &&
				e.target.matches(
					'input:not([type="password"]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), textarea'
				)
			) {
				const element = e.target;

				// Clear existing timer for this element
				if (inputChangeTimers.has(element)) {
					clearTimeout(inputChangeTimers.get(element));
				}

				// Set new timer (debounce 2 seconds)
				const timer = setTimeout(() => {
					this.trackEvent({
						eventType: "input_change",
						elementId: element.id,
						elementName:
							element.name ||
							element.placeholder ||
							element.getAttribute("aria-label") ||
							"unnamed_input",
						additionalData: {
							inputType: element.type || element.tagName.toLowerCase(),
							valueLength: element.value?.length || 0, // Privacy: length only
							formId: element.form?.id || null,
							formName: element.form?.name || null,
						},
					});
					inputChangeTimers.delete(element);
				}, 2000);

				inputChangeTimers.set(element, timer);
			}
		};
		document.addEventListener("input", this.listeners.inputChange, true);

		// Helper function to find label text for an input element
		const findLabelText = (element) => {
			// Try to find label by 'for' attribute
			if (element.id) {
				const label = document.querySelector(`label[for="${element.id}"]`);
				if (label) {
					return label.textContent?.trim() || null;
				}
			}
			// Try to find parent label
			const parentLabel = element.closest("label");
			if (parentLabel) {
				return parentLabel.textContent?.trim() || null;
			}
			// Try aria-label
			if (element.getAttribute("aria-label")) {
				return element.getAttribute("aria-label");
			}
			// Try data-label
			if (element.getAttribute("data-label")) {
				return element.getAttribute("data-label");
			}
			// Try finding label by name/id pattern
			if (element.name) {
				const labelById = document.querySelector(
					`label[for="${element.name}"]`
				);
				if (labelById) {
					return labelById.textContent?.trim() || null;
				}
			}
			return null;
		};

		// Track selection changes (radio buttons, checkboxes, select dropdowns, custom dropdowns)
		// This is immediate (not debounced) as selections are important interactions
		this.listeners.selectionChange = (e) => {
			const element = e.target;

			// Radio buttons (native HTML)
			if (element.type === "radio" && element.checked) {
				// Get all radio buttons in the same group to track what was deselected
				const radioGroup = element.form
					? Array.from(
							element.form.querySelectorAll(
								`input[type="radio"][name="${element.name}"]`
							)
						)
					: Array.from(
							document.querySelectorAll(
								`input[type="radio"][name="${element.name}"]`
							)
						);
				const previouslyChecked = radioGroup.find(
					(radio) => radio !== element && radio.checked
				);

				// Find the selected option name/label
				const selectedLabel = findLabelText(element);
				const selectedName =
					selectedLabel ||
					element.value ||
					element.getAttribute("aria-label") ||
					"unnamed_radio_option";

				this.trackEvent({
					eventType: "selection_change",
					elementId: element.id,
					elementName: element.name || "radio_group",
					additionalData: {
						selectionType: "radio",
						selectedValue: element.value,
						selectedName,
						groupName: element.name,
						previousValue: previouslyChecked?.value || null,
						previousName: previouslyChecked
							? findLabelText(previouslyChecked) ||
								previouslyChecked.value ||
								null
							: null,
						formId: element.form?.id || null,
						formName: element.form?.name || null,
					},
				});
			}

			// Checkboxes (native HTML)
			if (element.type === "checkbox") {
				// Find the checkbox label
				const checkboxLabel = findLabelText(element);
				const checkboxName =
					checkboxLabel ||
					element.name ||
					element.getAttribute("aria-label") ||
					element.value ||
					"unnamed_checkbox";

				this.trackEvent({
					eventType: "selection_change",
					elementId: element.id,
					elementName: checkboxName,
					additionalData: {
						selectionType: "checkbox",
						selectedValue:
							element.value || (element.checked ? "checked" : "unchecked"),
						selectedName: checkboxName,
						isChecked: element.checked,
						action: element.checked ? "checked" : "unchecked",
						formId: element.form?.id || null,
						formName: element.form?.name || null,
					},
				});
			}

			// Select dropdowns (native HTML select)
			if (element.tagName === "SELECT") {
				const selectedOption = element.options[element.selectedIndex];
				// Try to track previous value by checking if we can detect it
				const previousIndex = element.getAttribute("data-previous-index");
				const previousValue =
					previousIndex !== null
						? element.options[parseInt(previousIndex, 10)]?.value
						: null;
				const previousText =
					previousIndex !== null
						? element.options[parseInt(previousIndex, 10)]?.text?.trim() || null
						: null;

				const selectLabel = findLabelText(element);
				const selectName =
					selectLabel ||
					element.name ||
					element.getAttribute("aria-label") ||
					element.getAttribute("data-label") ||
					"unnamed_select";

				this.trackEvent({
					eventType: "selection_change",
					elementId: element.id,
					elementName: selectName,
					additionalData: {
						selectionType: "select",
						selectedValue: element.value,
						selectedText: selectedOption?.text?.trim() || null,
						selectedName: selectedOption?.text?.trim() || element.value || null,
						selectedIndex: element.selectedIndex,
						previousValue,
						previousText,
						totalOptions: element.options.length,
						formId: element.form?.id || null,
						formName: element.form?.name || null,
					},
				});

				// Store current index for next change detection
				element.setAttribute(
					"data-previous-index",
					element.selectedIndex.toString()
				);
			}

			// Track custom dropdowns (Radix UI, custom implementations)
			// These often use role="combobox" or role="listbox" with change events
			if (
				element.getAttribute("role") === "combobox" ||
				element.getAttribute("role") === "option" ||
				element.closest('[role="combobox"], [role="listbox"]')
			) {
				const dropdown =
					element.closest('[role="combobox"], [role="listbox"]') || element;
				const selectedValue =
					element.getAttribute("data-value") ||
					element.getAttribute("value") ||
					element.textContent?.trim() ||
					null;

				this.trackEvent({
					eventType: "selection_change",
					elementId: dropdown.id || element.id,
					elementName:
						dropdown.getAttribute("aria-label") ||
						element.getAttribute("aria-label") ||
						dropdown.getAttribute("data-label") ||
						"unnamed_dropdown",
					additionalData: {
						selectionType: "custom_dropdown",
						selectedValue,
						selectedText: element.textContent?.trim() || null,
						selectedName: element.textContent?.trim() || selectedValue || null,
						role: element.getAttribute("role") || dropdown.getAttribute("role"),
						formId: element.form?.id || dropdown.closest("form")?.id || null,
						formName:
							element.form?.name || dropdown.closest("form")?.name || null,
					},
				});
			}
		};
		document.addEventListener("change", this.listeners.selectionChange, true);

		// Track Radix UI components (RadioGroup, Checkbox, Switch)
		// These use data-state="checked" and don't always fire change events
		// Handle both click and touch for mobile compatibility
		const handleRadixSelection = (e) => {
			const { target } = e;

			// Skip if this is a dropdown trigger (already handled by handleInteraction)
			const dropdownTrigger = target.closest(
				'button[data-radix-dropdown-menu-trigger], button[data-radix-popover-trigger], [data-radix-dropdown-menu-trigger], [data-radix-popover-trigger], button[aria-haspopup="menu"], [role="button"][aria-haspopup="menu"]'
			);
			if (dropdownTrigger) {
				return; // Already handled by handleInteraction
			}

			// Radix UI RadioGroup item (role="radio")
			const radioItem = target.closest('[role="radio"]');
			if (radioItem) {
				// Use setTimeout to check state after Radix UI updates
				setTimeout(() => {
					const radioGroup = radioItem.closest('[role="radiogroup"]');
					const isChecked =
						radioItem.getAttribute("data-state") === "checked" ||
						radioItem.getAttribute("aria-checked") === "true";

					if (isChecked) {
						// Find the label/name for this radio item
						// Look for Label component nearby or parent text
						const radioLabel =
							radioItem.getAttribute("aria-label") ||
							radioItem.closest("label")?.textContent?.trim() ||
							radioItem.parentElement
								?.querySelector("label")
								?.textContent?.trim() ||
							radioItem.parentElement
								?.querySelector('[class*="Label"]')
								?.textContent?.trim() ||
							radioItem.getAttribute("value") ||
							radioItem.textContent?.trim() ||
							"unnamed_radio_option";

						const groupName =
							radioGroup?.getAttribute("aria-label") ||
							radioGroup?.getAttribute("name") ||
							radioGroup?.id ||
							"radio_group";

						// Find previously selected radio in the group (if any)
						let previousName = null;
						if (radioGroup) {
							const allRadios = radioGroup.querySelectorAll('[role="radio"]');
							for (const radio of allRadios) {
								if (
									radio !== radioItem &&
									(radio.getAttribute("data-state") === "checked" ||
										radio.getAttribute("aria-checked") === "true")
								) {
									previousName =
										radio.getAttribute("aria-label") ||
										radio.closest("label")?.textContent?.trim() ||
										radio.parentElement
											?.querySelector("label")
											?.textContent?.trim() ||
										radio.parentElement
											?.querySelector('[class*="Label"]')
											?.textContent?.trim() ||
										radio.getAttribute("value") ||
										null;
									break;
								}
							}
						}

						this.trackEvent({
							eventType: "selection_change",
							elementId: radioItem.id || null,
							elementName: groupName,
							additionalData: {
								selectionType: "radio_radix",
								selectedValue:
									radioItem.getAttribute("value") || radioItem.id || null,
								selectedName: radioLabel,
								groupName,
								previousName,
								formId: radioGroup?.closest("form")?.id || null,
								formName: radioGroup?.closest("form")?.name || null,
							},
						});
					}
				}, 10); // Small delay to let Radix UI update state
				return;
			}

			// Radix UI Checkbox (button with data-state="checked")
			const checkboxItem = target.closest(
				'button[data-radix-checkbox-root], [data-state][role="checkbox"]'
			);
			if (checkboxItem) {
				// Use setTimeout to check state after Radix UI updates
				setTimeout(() => {
					const isChecked =
						checkboxItem.getAttribute("data-state") === "checked" ||
						checkboxItem.getAttribute("aria-checked") === "true";

					const checkboxLabel =
						checkboxItem.getAttribute("aria-label") ||
						checkboxItem.closest("label")?.textContent?.trim() ||
						checkboxItem.parentElement
							?.querySelector("label")
							?.textContent?.trim() ||
						checkboxItem.parentElement
							?.querySelector('[class*="Label"]')
							?.textContent?.trim() ||
						checkboxItem
							.closest('[role="group"]')
							?.getAttribute("aria-label") ||
						checkboxItem.getAttribute("data-label") ||
						"unnamed_checkbox";

					this.trackEvent({
						eventType: "selection_change",
						elementId: checkboxItem.id || null,
						elementName: checkboxLabel,
						additionalData: {
							selectionType: "checkbox_radix",
							selectedValue:
								checkboxItem.getAttribute("value") ||
								(isChecked ? "checked" : "unchecked"),
							selectedName: checkboxLabel,
							isChecked,
							action: isChecked ? "checked" : "unchecked",
							formId: checkboxItem.closest("form")?.id || null,
							formName: checkboxItem.closest("form")?.name || null,
						},
					});
				}, 10); // Small delay to let Radix UI update state
				return;
			}

			// Radix UI Switch (button with data-state="checked")
			const switchItem = target.closest(
				'button[data-radix-switch-root], [data-state][role="switch"]'
			);
			if (switchItem) {
				// Use setTimeout to check state after Radix UI updates
				setTimeout(() => {
					const isChecked =
						switchItem.getAttribute("data-state") === "checked" ||
						switchItem.getAttribute("aria-checked") === "true";

					const switchLabel =
						switchItem.getAttribute("aria-label") ||
						switchItem.closest("label")?.textContent?.trim() ||
						switchItem.parentElement
							?.querySelector("label")
							?.textContent?.trim() ||
						switchItem.parentElement
							?.querySelector('[class*="Label"]')
							?.textContent?.trim() ||
						switchItem.closest('[role="group"]')?.getAttribute("aria-label") ||
						switchItem.getAttribute("data-label") ||
						"unnamed_switch";

					this.trackEvent({
						eventType: "selection_change",
						elementId: switchItem.id || null,
						elementName: switchLabel,
						additionalData: {
							selectionType: "switch",
							selectedValue: isChecked ? "on" : "off",
							selectedName: switchLabel,
							isChecked,
							action: isChecked ? "turned_on" : "turned_off",
							formId: switchItem.closest("form")?.id || null,
							formName: switchItem.closest("form")?.name || null,
						},
					});
				}, 10); // Small delay to let Radix UI update state
			}
		};
		this.listeners.radixSelectionClick = (e) => handleRadixSelection(e);
		this.listeners.radixSelectionTouch = (e) => {
			// Handle touch events for Radix UI components on mobile
			// Use a slight delay to allow state updates
			setTimeout(() => handleRadixSelection(e), 50);
		};

		document.addEventListener(
			"click",
			this.listeners.radixSelectionClick,
			true
		);
		document.addEventListener("touchend", this.listeners.radixSelectionTouch, {
			passive: true,
			capture: true,
		});

		// Track dropdown menu item selections
		// Radix UI DropdownMenuItem uses role="menuitem"
		// Handle both click and touch for mobile compatibility
		const handleDropdownItemSelection = (e) => {
			const { target } = e;

			// Check if click is on a dropdown menu item
			const menuItem = target.closest(
				'[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]'
			);

			if (menuItem) {
				// Find the parent dropdown menu to get context
				const dropdownMenu = menuItem.closest('[role="menu"]');
				const dropdownTrigger = document.querySelector(
					'button[aria-expanded="true"][aria-haspopup="menu"], [data-radix-dropdown-menu-trigger]'
				);

				// Get the selected item name/text
				const selectedItemName =
					menuItem.textContent?.trim() ||
					menuItem.getAttribute("aria-label") ||
					menuItem.getAttribute("data-label") ||
					menuItem.getAttribute("value") ||
					"unnamed_item";

				// Get dropdown context
				const dropdownId = dropdownTrigger?.id || dropdownMenu?.id || null;
				const dropdownName =
					dropdownTrigger?.getAttribute("aria-label") ||
					dropdownTrigger?.getAttribute("data-label") ||
					dropdownTrigger?.querySelector("img[alt]")?.getAttribute("alt") ||
					dropdownMenu?.getAttribute("aria-label") ||
					"dropdown_menu";

				this.trackEvent({
					eventType: "dropdown_selection",
					elementId: dropdownId,
					elementName: dropdownName,
					additionalData: {
						selectedItemName,
						selectedItemId: menuItem.id || null,
						dropdownType: "menu",
					},
				});
			}
		};

		this.listeners.dropdownItemClick = (e) => handleDropdownItemSelection(e);
		this.listeners.dropdownItemTouch = (e) => {
			// Handle touch events for dropdown items on mobile
			handleDropdownItemSelection(e);
		};

		document.addEventListener("click", this.listeners.dropdownItemClick, true);
		document.addEventListener("touchend", this.listeners.dropdownItemTouch, {
			passive: true,
			capture: true,
		});

		// Track video play
		this.listeners.play = (e) => {
			if (e.target.tagName === "VIDEO") {
				const video = e.target;
				const videoUrl = video.currentSrc || video.src;
				const eventKey = `${videoUrl}_play`;
				const now = Date.now();
				const currentTime = video.currentTime || 0;

				// CRITICAL: For infinite looping videos, only track the FIRST play event per video URL
				// Once we've tracked a play for this video URL, skip all subsequent play events
				// This prevents infinite tracking when videos loop back
				if (this.trackedVideoPlays.has(videoUrl)) {
					// Already tracked this video's initial play - skip loop replays
					return;
				}

				// Deduplication: Skip if same event tracked within last 1 second (safety check)
				const lastEventTime = this.lastVideoEvents.get(eventKey);
				if (lastEventTime && now - lastEventTime < 1000) {
					return; // Skip duplicate event
				}

				// Detect video loop/replay: if currentTime is near 0 and we've tracked this video before,
				// it's likely a loop/restart, not a new play
				const lastPlayTime = this.lastVideoPlayTimes.get(videoUrl);
				const isLoopReplay = lastPlayTime !== undefined && currentTime < 2.0; // Less than 2 seconds = likely a loop

				if (isLoopReplay) {
					// Update the play time but don't track the event
					this.lastVideoPlayTimes.set(videoUrl, currentTime);
					return; // Skip tracking loop/replay events
				}

				// Track the event (first play for this video URL)
				this.lastVideoEvents.set(eventKey, now);
				this.lastVideoPlayTimes.set(videoUrl, currentTime);
				this.trackedVideoPlays.add(videoUrl); // Mark this video as tracked

				const videoName =
					video.getAttribute("data-name") ||
					video.getAttribute("aria-label") ||
					video.id ||
					"unnamed_video";

				this.trackEvent({
					eventType: "video_play",
					videoUrl,
					elementId: video.id,
					elementName: videoName,
					additionalData: {
						videoDuration: video.duration || null,
						autoplay: video.autoplay || false,
					},
				});

				// Removed duplicate video_interaction event - video_play is sufficient
			}
		};
		document.addEventListener("play", this.listeners.play, true);

		// Track video pause - DISABLED: Only track video_play events, not pause/ended
		this.listeners.pause = (e) => {
			// Skip tracking pause events - only track which videos played
		};
		document.addEventListener("pause", this.listeners.pause, true);

		// Track video ended - DISABLED: Only track video_play events, not pause/ended
		this.listeners.ended = (e) => {
			// Skip tracking ended events - only track which videos played
		};
		document.addEventListener("ended", this.listeners.ended, true);

		// Cleanup old video event deduplication entries every 30 seconds
		// This prevents memory leaks from the deduplication map
		this.videoEventCleanupInterval = setInterval(() => {
			const now = Date.now();
			const maxAge = 30000; // 30 seconds
			for (const [key, timestamp] of this.lastVideoEvents.entries()) {
				if (now - timestamp > maxAge) {
					this.lastVideoEvents.delete(key);
				}
			}
			// Also cleanup old play time tracking (keep for 5 minutes to detect loops)
			const playTimeMaxAge = 300000; // 5 minutes
			for (const [
				videoUrl,
				lastPlayTime,
			] of this.lastVideoPlayTimes.entries()) {
				// If we haven't seen this video play recently, remove it
				const lastEventTime = this.lastVideoEvents.get(`${videoUrl}_play`);
				if (lastEventTime && now - lastEventTime > playTimeMaxAge) {
					this.lastVideoPlayTimes.delete(videoUrl);
					// Also remove from tracked plays set to allow re-tracking if video is loaded again
					this.trackedVideoPlays.delete(videoUrl);
				}
			}
		}, 30000); // Run cleanup every 30 seconds

		// Track scroll depth (debounced)
		const scrollThresholds = [25, 50, 75, 100];
		this.listeners.scroll = this.debounce(() => {
			const windowHeight = window.innerHeight;
			const documentHeight = document.documentElement.scrollHeight;
			const scrollTop =
				window.pageYOffset || document.documentElement.scrollTop;
			const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

			for (const threshold of scrollThresholds) {
				if (scrollPercent >= threshold && this.scrollDepth < threshold) {
					this.scrollDepth = threshold;
					this.trackEvent({
						eventType: "scroll_depth",
						elementName: `${threshold}%`,
					});
				}
			}
		}, 500);
		window.addEventListener("scroll", this.listeners.scroll);

		// Track page visibility changes
		this.listeners.visibilitychange = () => {
			this.trackEvent({
				eventType: document.hidden ? "page_hidden" : "page_visible",
			});
		};
		document.addEventListener(
			"visibilitychange",
			this.listeners.visibilitychange
		);

		// Track page unload
		this.listeners.beforeunload = () => {
			// Use sendBeacon for page_unload (most reliable during page unload)
			const currentUrl = window.location.href;
			const event = {
				sessionId: this.sessionId,
				memberId: this.memberId,
				eventType: "page_unload",
				pageUrl: currentUrl,
				pageTitle: document.title,
				refererUrl: document.referrer,
				createdAt: new Date().toISOString(),
			};

			// Only include sessionId in dev mode (exclude in staging/production)
			const isDev = import.meta.env.MODE === "development";
			const payload = { events: [event] };
			if (isDev) {
				payload.sessionid = this.sessionId;
			}

			// Send immediately with sendBeacon (synchronous, doesn't block navigation)
			const blob = new Blob([JSON.stringify(payload)], {
				type: "application/json",
			});
			const sent = navigator.sendBeacon(`${API_URL}/trackevents/`, blob);
			// console.log("📡 Page unload event sent via sendBeacon:", sent);

			// Also flush any failed events
			this.flush(true);
		};
		window.addEventListener("beforeunload", this.listeners.beforeunload);

		// Track JavaScript errors
		this.listeners.error = (event) => {
			const errorMessage = event.message || "Unknown error";
			const errorType = event.error?.name || "Error";

			this.trackEvent({
				eventType: "error",
				elementName: errorMessage,
				additionalData: {
					filename: event.filename,
					lineno: event.lineno,
					colno: event.colno,
					stack: event.error?.stack?.substring(0, 500), // Limit stack trace length
					errorType,
					errorCode: errorType, // Map error type as code
				},
			});
		};
		window.addEventListener("error", this.listeners.error);

		// Track unhandled promise rejections
		this.listeners.unhandledrejection = (event) => {
			this.trackEvent({
				eventType: "error",
				elementName: "Unhandled Promise Rejection",
				additionalData: {
					reason: event.reason?.toString().substring(0, 200),
					stack: event.reason?.stack?.substring(0, 500),
				},
			});
		};
		window.addEventListener(
			"unhandledrejection",
			this.listeners.unhandledrejection
		);

		// Track modal/dialog open/close (common patterns)
		// Listen for modal open/close events from common libraries
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.addedNodes.forEach((node) => {
					if (node.nodeType === 1) {
						// Element node
						const modal = node.matches?.('[role="dialog"]')
							? node
							: node.querySelector?.('[role="dialog"]');
						if (modal && !modal.hasAttribute("data-tracked-modal")) {
							modal.setAttribute("data-tracked-modal", "true");
							const modalName =
								modal.getAttribute("aria-label") ||
								modal.getAttribute("data-modal-name") ||
								modal.id ||
								"modal";
							this.trackEvent({
								eventType: "modal_open",
								elementId: modal.id,
								elementName: modalName,
							});
						}
					}
				});

				mutation.removedNodes.forEach((node) => {
					if (node.nodeType === 1) {
						const modal = node.matches?.('[role="dialog"]')
							? node
							: node.querySelector?.('[role="dialog"]');
						if (modal && modal.hasAttribute("data-tracked-modal")) {
							const modalName =
								modal.getAttribute("aria-label") ||
								modal.getAttribute("data-modal-name") ||
								modal.id ||
								"modal";
							this.trackEvent({
								eventType: "modal_close",
								elementId: modal.id,
								elementName: modalName,
							});
							modal.removeAttribute("data-tracked-modal");
						}
					}
				});
			});
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		// Store observer for cleanup
		this.modalObserver = observer;
	}

	/**
	 * Remove all global event listeners (cleanup)
	 */
	removeGlobalListeners() {
		if (this.listeners.click) {
			document.removeEventListener("click", this.listeners.click, true);
		}
		if (this.listeners.touchstart) {
			document.removeEventListener(
				"touchstart",
				this.listeners.touchstart,
				true
			);
		}
		if (this.listeners.submit) {
			document.removeEventListener("submit", this.listeners.submit, true);
		}
		if (this.listeners.inputFocus) {
			document.removeEventListener("focus", this.listeners.inputFocus, true);
		}
		if (this.listeners.inputBlur) {
			document.removeEventListener("blur", this.listeners.inputBlur, true);
		}
		if (this.listeners.inputChange) {
			document.removeEventListener("input", this.listeners.inputChange, true);
		}
		if (this.listeners.play) {
			document.removeEventListener("play", this.listeners.play, true);
		}
		if (this.listeners.pause) {
			document.removeEventListener("pause", this.listeners.pause, true);
		}
		if (this.listeners.ended) {
			document.removeEventListener("ended", this.listeners.ended, true);
		}
		if (this.listeners.scroll) {
			window.removeEventListener("scroll", this.listeners.scroll);
		}
		if (this.listeners.visibilitychange) {
			document.removeEventListener(
				"visibilitychange",
				this.listeners.visibilitychange
			);
		}
		if (this.listeners.beforeunload) {
			window.removeEventListener("beforeunload", this.listeners.beforeunload);
		}
		if (this.listeners.error) {
			window.removeEventListener("error", this.listeners.error);
		}
		if (this.listeners.unhandledrejection) {
			window.removeEventListener(
				"unhandledrejection",
				this.listeners.unhandledrejection
			);
		}
		if (this.listeners.selectionChange) {
			document.removeEventListener(
				"change",
				this.listeners.selectionChange,
				true
			);
		}
		if (this.listeners.radixSelectionClick) {
			document.removeEventListener(
				"click",
				this.listeners.radixSelectionClick,
				true
			);
		}
		if (this.listeners.radixSelectionTouch) {
			document.removeEventListener(
				"touchend",
				this.listeners.radixSelectionTouch,
				true
			);
		}
		if (this.listeners.dropdownItemClick) {
			document.removeEventListener(
				"click",
				this.listeners.dropdownItemClick,
				true
			);
		}
		if (this.listeners.dropdownItemTouch) {
			document.removeEventListener(
				"touchend",
				this.listeners.dropdownItemTouch,
				true
			);
		}
		if (this.modalObserver) {
			this.modalObserver.disconnect();
			this.modalObserver = null;
		}
		if (this.videoEventCleanupInterval) {
			clearInterval(this.videoEventCleanupInterval);
			this.videoEventCleanupInterval = null;
		}
		// Clear deduplication maps
		this.lastVideoEvents.clear();
		this.lastVideoPlayTimes.clear();
		this.trackedVideoPlays.clear();

		// Clear listener references
		Object.keys(this.listeners).forEach((key) => {
			this.listeners[key] = null;
		});
	}

	/**
	 * Send a single event immediately (real-time)
	 * @param {Object} event - The event object to send
	 * @returns {Promise<void>}
	 */
	async sendEvent(event) {
		// Ensure event has current page info
		const currentUrl = window.location.href;
		const currentTitle = document.title;

		// For page_unload events, keep original page URL
		const eventToSend =
			event.eventType === "page_unload"
				? event
				: {
						...event,
						// Always use current page URL
						pageUrl: currentUrl,
						pageTitle: currentTitle,
						// Preserve original as referer if different
						refererUrl:
							event.pageUrl !== currentUrl
								? event.refererUrl || event.pageUrl
								: event.refererUrl || document.referrer,
					};

		// console.log(
		// 	"🚀 Sending event:",
		// 	event.eventType,
		// 	"to",
		// 	`${API_URL}/trackevents/`
		// );

		try {
			// Only include sessionId in dev mode (exclude in staging/production)
			const isDev = import.meta.env.MODE === "development";
			// const isDev = false;
			const payload = {
				events: [eventToSend], // Send as array with single event
			};

			// Add sessionId only in development mode
			if (isDev) {
				payload.sessionid = this.sessionId;
			}

			// Use apiClient (axios) - CSRF token handled automatically
			const response = await apiClient.post("/trackevents/", payload);

			// console.log("✅ Event sent successfully:", event.eventType);
		} catch (error) {
			// console.error("❌ Error sending event:", error);
			// Only queue failed events for retry (not normal operation)
			this.eventQueue.push(eventToSend);
			this.saveQueue();
			// console.log(
			// 	"📦 Event queued for retry. Queue size:",
			// 	this.eventQueue.length
			// );
		}
	}

	/**
	 * Flush queued events (only used for failed events that need retry, or on page unload)
	 * @param {boolean} isSync - If true, use sendBeacon for synchronous sends (e.g., page unload)
	 * @returns {Promise<void>}
	 */
	async flush(isSync = false) {
		if (this.eventQueue.length === 0) {
			return;
		}

		// ALWAYS get current page info right before sending
		const currentUrl = window.location.href;
		const currentTitle = document.title;

		const eventsToSend = this.eventQueue.map((event) => {
			// For page_unload events, keep original page URL
			if (event.eventType === "page_unload") {
				return event;
			}

			// For ALL other events, ALWAYS use current page URL
			const originalPageUrl = event.pageUrl;
			const shouldUpdate = originalPageUrl !== currentUrl;

			return {
				...event,
				pageUrl: currentUrl,
				pageTitle: currentTitle,
				refererUrl: shouldUpdate
					? event.refererUrl || originalPageUrl
					: event.refererUrl || document.referrer,
			};
		});

		this.eventQueue = [];
		this.saveQueue(); // Clear persisted queue

		// console.log(
		// 	"🚀 Flushing",
		// 	eventsToSend.length,
		// 	"queued events to",
		// 	`${API_URL}/trackevents/`
		// );

		try {
			// Only include sessionId in dev mode (exclude in staging/production)
			const isDev = import.meta.env.MODE === "development";
			// const isDev = false;

			if (isSync) {
				// Use sendBeacon for synchronous sends (e.g., page unload)
				const payload = { events: eventsToSend };
				if (isDev) {
					payload.sessionid = this.sessionId;
				}
				const blob = new Blob([JSON.stringify(payload)], {
					type: "application/json",
				});
				const sent = navigator.sendBeacon(`${API_URL}/trackevents/`, blob);
				// console.log("📡 sendBeacon result:", sent);
			} else {
				// Use apiClient (axios) for normal sends
				const payload = {
					events: eventsToSend,
				};

				// Add sessionId only in development mode
				if (isDev) {
					payload.sessionid = this.sessionId;
				}

				const response = await apiClient.post("/trackevents/", payload);

				// console.log("✅ Response status:", response.status);
			}
		} catch (error) {
			// console.error("Error sending queued events:", error);
			// Re-queue failed events
			this.eventQueue.unshift(...eventsToSend);
			this.saveQueue();
		}
	}

	// Removed scheduleFlush and cancelScheduledFlush - events now send immediately on user interactions

	/**
	 * Save queue to localStorage (only for failed events that need retry)
	 */
	saveQueue() {
		// Only save if there are failed events to retry
		if (this.eventQueue.length === 0) {
			try {
				localStorage.removeItem("eventTracker_queue");
			} catch (error) {
				// Ignore errors when clearing
			}
			return;
		}

		try {
			localStorage.setItem(
				"eventTracker_queue",
				JSON.stringify(this.eventQueue)
			);
		} catch (error) {
			// console.warn("Failed to save failed events to localStorage:", error);
		}
	}

	/**
	 * Load failed events from localStorage (for retry)
	 * Only failed events from previous session that need to be retried
	 */
	loadQueue() {
		try {
			const saved = localStorage.getItem("eventTracker_queue");
			if (saved) {
				const parsedQueue = JSON.parse(saved);
				// Only load events from current session
				const currentSessionId = this.sessionId || null;
				if (currentSessionId) {
					this.eventQueue = parsedQueue.filter(
						(event) => event.sessionId === currentSessionId
					);
				} else {
					// If not initialized yet, store temporarily
					this.eventQueue = parsedQueue;
				}

				if (this.eventQueue.length > 0) {
					// console.log(
					// 	`📦 Loaded ${this.eventQueue.length} failed events from localStorage for retry`
					// );
				}
			}
		} catch (error) {
			// console.warn("Failed to load failed events from localStorage:", error);
			this.eventQueue = [];
		}
	}

	/**
	 * Reset scroll depth (call on route change)
	 */
	resetScrollDepth() {
		this.scrollDepth = 0;
	}

	/**
	 * Reset tracked looping videos (called on page navigation)
	 */
	resetTrackedLoopingVideos() {
		// No-op - kept for API compatibility
	}

	// Removed updateQueuedEventsPageUrl() - no longer needed since events are sent in real-time
	// Queue is only used for failed events that need retry

	/**
	 * Debounce utility
	 */
	debounce(func, wait) {
		let timeout;
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func.apply(this, args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	}

	/**
	 * Update session/member info (e.g., after login)
	 */
	updateSession(sessionId, memberId) {
		this.sessionId = sessionId;
		this.memberId = memberId;

		// Only track session update if initialized
		if (this.isInitialized) {
			this.trackEvent({
				eventType: "session_update",
			});
		}
	}

	/**
	 * Clear the tracker (e.g., on logout)
	 */
	clear() {
		// Flush any failed events that were queued for retry
		if (this.eventQueue.length > 0) {
			this.flush(true);
		}

		this.removeGlobalListeners(); // Clean up listeners

		this.sessionId = null;
		this.memberId = null;
		this.isInitialized = false;
		this.scrollDepth = 0;

		// Clear localStorage
		try {
			localStorage.removeItem("eventTracker_queue");
		} catch (error) {
			// console.warn("Failed to clear event queue from localStorage:", error);
		}
	}
}

// Export singleton instance
export const eventTracker = new EventTracker();
